/**
 * CRUD toggles per resource – reuse UX from MunicipalityAccessManager.
 *
 * Phase 2.5 – USER_ROLES.md
 */

import React from 'react';
import { Table, Checkbox, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { ResourceKey, ResourcePermissions, RolePermissions } from '@municipality/types';
import { RESOURCE_KEYS } from '@municipality/types';

const { Text } = Typography;

interface RolePermissionsEditorProps {
  value?: RolePermissions;
  onChange?: (perms: RolePermissions) => void;
  disabled?: boolean;
}

const CRUD_KEYS: (keyof ResourcePermissions)[] = ['create', 'read', 'update', 'delete'];

export const RolePermissionsEditor: React.FC<RolePermissionsEditorProps> = ({
  value = {},
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('userRoles');

  const handleChange = (resource: ResourceKey, crudKey: keyof ResourcePermissions, checked: boolean) => {
    const current = value[resource] ?? {};
    const updated: ResourcePermissions = { ...current, [crudKey]: checked };
    onChange?.({ ...value, [resource]: updated });
  };

  const columns = [
    {
      title: t('userRoles:permissions.resource'),
      dataIndex: 'resource',
      key: 'resource',
      width: 200,
      fixed: 'left' as const,
      render: (resource: ResourceKey) => (
        <Text strong>
          {t(`userRoles:permissions.${resource}`)}
        </Text>
      ),
    },
    ...CRUD_KEYS.map((crudKey) => ({
      title: t(`userRoles:permissions.${crudKey}`),
      dataIndex: crudKey,
      key: crudKey,
      width: 90,
      align: 'center' as const,
      render: (_: unknown, record: { resource: ResourceKey }) => (
        <Checkbox
          checked={!!(value[record.resource]?.[crudKey])}
          onChange={(e) => handleChange(record.resource, crudKey, e.target.checked)}
          disabled={disabled}
        />
      ),
    })),
  ];

  const dataSource = RESOURCE_KEYS.map((resource) => ({ resource, key: resource }));

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      size="small"
      scroll={{ x: 600 }}
    />
  );
};
